import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuthStore } from "../store/authStore";
import { useThemeStore, THEME_VARS } from "../store/themeStore";
import { useLangStore } from "../i18n/langStore";
import TwoFactorSetup from "../components/TwoFactorSetup";
import { useT } from "../i18n";

export default function LoginPage() {
  const t = useT();
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [step, setStep]         = useState<"login" | "2fa" | "enroll">("login");
  const [mfaToken, setMfaToken] = useState("");
  const [code2fa, setCode2fa]   = useState("");
  const { setTokens }           = useAuthStore();
  const { setTheme }            = useThemeStore();
  const navigate                = useNavigate();
  const emailRef                = useRef<HTMLInputElement>(null);

  useEffect(() => { emailRef.current?.focus(); }, []);

  const finishLogin = (data: any) => {
    setTokens(data.access_token, data.refresh_token, data.role, data.email || email, data.first_name, data.last_name, data.organization_id, data.organization_name, data.plan, data.primary_color, data.logo_url);
    // Idioma = servidor (fuente de verdad cross-device); persiste en langStore.
    if (data.lang === "es" || data.lang === "en") useLangStore.getState().setLang(data.lang);
    setTheme("light");
    Object.entries(THEME_VARS["light"]).forEach(([k, v]) => document.documentElement.style.setProperty(k, v));
    navigate(data.role === "superadmin" ? "/superadmin/dashboard" : "/dashboard");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const params = new URLSearchParams({ username: email, password });
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      if (!res.ok) throw new Error(t("auth.wrongCredentials"));
      const data = await res.json();
      if (data.requires_2fa) { setMfaToken(data.mfa_token); setStep("2fa"); return; }
      if (data.requires_enrollment) { setMfaToken(data.mfa_token); setStep("enroll"); return; }
      finishLogin(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t("auth.unknownError"));
    } finally {
      setLoading(false);
    }
  };

  const submit2fa = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/2fa/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mfa_token: mfaToken, code: code2fa }),
      });
      if (!res.ok) throw new Error(t("auth.wrong2faCode"));
      finishLogin(await res.json());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t("auth.unknownError"));
    } finally {
      setLoading(false);
    }
  };

  const G = "#0891b2";

  return (
    <>
      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmer {
          0%   { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        .li-input {
          width: 100%;
          padding: 12px 16px;
          background: rgba(255,255,255,0.65);
          border: 1px solid rgba(255,255,255,0.85);
          border-radius: 12px;
          color: #0c1f26;
          font-size: 0.92rem;
          outline: none;
          transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
          backdrop-filter: blur(8px);
          box-sizing: border-box;
        }
        .li-input::placeholder { color: rgba(12,31,38,0.35); }
        .li-input:focus {
          border-color: rgba(8,145,178,0.7);
          background: rgba(255,255,255,0.82);
          box-shadow: 0 0 0 3px rgba(8,145,178,0.12), inset 0 1px 0 rgba(255,255,255,0.9);
        }
        .li-input:-webkit-autofill,
        .li-input:-webkit-autofill:focus {
          -webkit-box-shadow: 0 0 0 1000px rgba(236,248,252,0.95) inset;
          -webkit-text-fill-color: #0c1f26 !important;
          caret-color: #0c1f26;
        }
        .li-btn {
          width: 100%;
          padding: 13px;
          border: none;
          border-radius: 12px;
          font-size: 0.97rem;
          font-weight: 700;
          letter-spacing: 0.4px;
          cursor: pointer;
          position: relative;
          overflow: hidden;
          transition: transform 0.15s, box-shadow 0.2s;
          background: linear-gradient(135deg, #06b6d4 0%, #0891b2 50%, #0e7490 100%);
          color: #fff;
          box-shadow: 0 4px 20px rgba(8,145,178,0.40), inset 0 1px 0 rgba(255,255,255,0.2);
        }
        .li-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent);
          background-size: 400px 100%;
          animation: shimmer 2.4s linear infinite;
        }
        .li-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 8px 28px rgba(8,145,178,0.50), inset 0 1px 0 rgba(255,255,255,0.25);
        }
        .li-btn:active:not(:disabled) { transform: translateY(0); }
        .li-btn:disabled { opacity: 0.6; cursor: not-allowed; }

        .li-pass-toggle {
          position: absolute; right: 13px; top: 50%;
          transform: translateY(-50%);
          background: none; border: none; cursor: pointer;
          color: rgba(12,31,38,0.38); padding: 4px;
          display: flex; align-items: center;
          transition: color 0.2s;
        }
        .li-pass-toggle:hover { color: rgba(12,31,38,0.75); }

        .li-label {
          display: block;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.7px;
          text-transform: uppercase;
          color: rgba(12,31,38,0.50);
          margin-bottom: 6px;
        }
        .li-error {
          display: flex; align-items: center; gap: 8px;
          background: rgba(220,38,38,0.08);
          border: 1px solid rgba(220,38,38,0.25);
          border-radius: 10px; padding: 10px 14px;
          color: #dc2626; font-size: 0.84rem;
          margin-bottom: 1.25rem;
          animation: fadeSlideUp 0.3s ease;
        }
      `}</style>

      {/* Background */}
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#e7f1f4",
        position: "relative",
        overflow: "hidden",
      }}>
        {/* Glows estáticos de fondo (lo que el cristal refracta) — como AppShell */}
        <div aria-hidden style={{
          position: "absolute", top: "-12%", right: "8%", width: 620, height: 620,
          background: "radial-gradient(circle closest-side, rgba(8,145,178,0.30), transparent 70%)",
          filter: "blur(60px)", pointerEvents: "none",
        }} />
        <div aria-hidden style={{
          position: "absolute", bottom: "-18%", left: "22%", width: 560, height: 560,
          background: "radial-gradient(circle closest-side, rgba(124,58,237,0.14), transparent 70%)",
          filter: "blur(60px)", pointerEvents: "none",
        }} />

        {/* Card */}
        <div style={{
          position: "relative",
          width: "100%",
          maxWidth: 420,
          margin: "1.5rem",
          animation: "fadeSlideUp 0.5s cubic-bezier(0.22,1,0.36,1) both",
        }}>
          {/* Glow ring */}
          <div style={{
            position: "absolute", inset: -1, borderRadius: 26,
            background: "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(8,145,178,0.2) 50%, rgba(255,255,255,0.7) 100%)",
            zIndex: 0, filter: "blur(0.5px)",
          }} />

          {/* Card body */}
          <div style={{
            position: "relative", zIndex: 1,
            background: "rgba(255,255,255,0.60)",
            backdropFilter: "blur(28px) saturate(180%)",
            WebkitBackdropFilter: "blur(28px) saturate(180%)",
            borderRadius: 24,
            border: "1px solid rgba(255,255,255,0.85)",
            boxShadow: "0 24px 64px rgba(8,145,178,0.13), inset 0 1px 0 rgba(255,255,255,0.95)",
            padding: "2.5rem 2.25rem",
            overflow: "hidden",
          }}>

            {/* Top shimmer line */}
            <div style={{
              position: "absolute", top: 0, left: "15%", right: "15%", height: 1,
              background: "linear-gradient(90deg, transparent, rgba(255,255,255,1), transparent)",
            }} />

            {/* Inner light */}
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, height: 130,
              background: "linear-gradient(180deg, rgba(255,255,255,0.35) 0%, transparent 100%)",
              borderRadius: "24px 24px 0 0", pointerEvents: "none",
            }} />

            {/* Logo */}
            <div style={{ display: "flex", justifyContent: "center", marginBottom: "0.75rem" }}>
              <img
                src="/helixscan-logo.png"
                alt="HelixScan"
                style={{ height: 40, objectFit: "contain", display: "block" }}
              />
            </div>

            {/* Title */}
            <div style={{ textAlign: "center", marginBottom: "1.25rem" }}>
              <h1 style={{
                margin: "0 0 10px", fontSize: "1.1rem", fontWeight: 700,
                color: "#0c1f26", letterSpacing: "0.2px",
              }}>
                {t("auth.taglineLine1")}<br />{t("auth.taglineLine2")}
              </h1>
              <p style={{
                margin: 0, fontSize: "0.8rem",
                color: "rgba(13,31,19,0.45)", letterSpacing: "0.2px",
              }}>
                {t("auth.subtitle")}
              </p>
            </div>

            {/* Divider */}
            <div style={{
              height: 1,
              background: `linear-gradient(90deg, transparent, ${G}88, transparent)`,
              marginBottom: "1.75rem",
            }} />

            {/* Error */}
            {error && (
              <div className="li-error">
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="9" stroke="#dc2626" strokeWidth="1.5"/>
                  <path d="M10 6v5M10 14h.01" stroke="#dc2626" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                {error}
              </div>
            )}

            {/* Form */}
            {step === "login" && (
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: "1.1rem" }}>
                <label className="li-label">{t("auth.email")}</label>
                <input
                  ref={emailRef}
                  className="li-input"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder={t("auth.emailPlaceholder")}
                  required
                  autoComplete="email"
                />
              </div>

              <div style={{ marginBottom: "1.75rem" }}>
                <label className="li-label">{t("auth.password")}</label>
                <div style={{ position: "relative" }}>
                  <input
                    className="li-input"
                    type={showPass ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                    style={{ paddingRight: 44 }}
                  />
                  <button
                    type="button"
                    className="li-pass-toggle"
                    onClick={() => setShowPass(v => !v)}
                    tabIndex={-1}
                    aria-label={showPass ? t("auth.hidePassword") : t("auth.showPassword")}
                  >
                    {showPass ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:"1rem" }}>
                <Link to="/forgot-password" style={{ fontSize:"0.8rem", color:"rgba(13,31,19,.45)", textDecoration:"none" }}
                  onMouseEnter={e => (e.currentTarget.style.color = G)}
                  onMouseLeave={e => (e.currentTarget.style.color = "rgba(13,31,19,.45)")}
                >
                  {t("auth.forgotPassword")}
                </Link>
              </div>

              <button type="submit" className="li-btn" disabled={loading}>
                {loading ? (
                  <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                      style={{ animation: "spin 0.8s linear infinite" }}>
                      <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
                    </svg>
                    {t("auth.verifying")}
                  </span>
                ) : t("auth.signIn")}
              </button>
            </form>
            )}

            {step === "2fa" && (
              <form onSubmit={submit2fa}>
                <label className="li-label">{t("auth.twoFactorTitle")}</label>
                <p style={{ fontSize: "0.85rem", color: "rgba(13,31,19,.6)", margin: "6px 0 14px" }}>
                  {t("auth.twoFactorDesc")}
                </p>
                <input
                  className="li-input" autoFocus inputMode="numeric" value={code2fa}
                  onChange={e => setCode2fa(e.target.value.replace(/[^0-9]/g, "").slice(0, 8))}
                  placeholder="123456" style={{ textAlign: "center", letterSpacing: 4, fontSize: "1.1rem", marginBottom: "1.25rem" }}
                />
                <button type="submit" className="li-btn" disabled={loading || code2fa.length < 6}>
                  {loading ? t("auth.verifying") : t("auth.verify")}
                </button>
                <button type="button" onClick={() => { setStep("login"); setCode2fa(""); setError(""); }}
                  style={{ marginTop: 12, background: "none", border: "none", color: "rgba(13,31,19,.5)", cursor: "pointer", fontSize: "0.82rem", width: "100%" }}>
                  {t("auth.back")}
                </button>
              </form>
            )}

            {step === "enroll" && (
              <div>
                <label className="li-label">{t("auth.enrollTitle")}</label>
                <p style={{ fontSize: "0.85rem", color: "rgba(13,31,19,.6)", margin: "6px 0 14px" }}>
                  {t("auth.enrollDesc")}
                </p>
                <TwoFactorSetup token={mfaToken} onDone={finishLogin} onCancel={() => { setStep("login"); setError(""); }} />
              </div>
            )}

            {/* Footer */}
            <p style={{
              marginTop: "1.75rem", textAlign: "center",
              fontSize: "0.72rem", color: "rgba(13,31,19,0.28)",
              letterSpacing: "0.3px",
            }}>
              {t("auth.copyright")}
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
